import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { DisruptionPnrGroupRow } from '@/lib/disruptions/types';
import { PnrSourceMark } from '@/components/provenance/pnr-source-mark';

// The passenger card's head (PO 2026-10-31): the PNR as the operator reads it to the
// passenger in front of them, how many travel and where, which one of the list it is, and
// ↑ ↓ to the previous / next PNR without closing the card. The rare actions live behind
// «Más acciones»; the dialog's own close button sits at the far right (`pr-16` keeps
// its room).

export interface PnrCardPosition {
  /** 1-based. */
  index: number;
  total: number;
  /** The list it was opened from («Te necesitan»); absent = the whole roster. */
  listLabel?: string | undefined;
}

export function PnrCardHeader({
  group,
  position,
  onPrev,
  onNext,
  moreActions,
}: {
  group: DisruptionPnrGroupRow;
  position?: PnrCardPosition | undefined;
  onPrev?: (() => void) | undefined;
  onNext?: (() => void) | undefined;
  moreActions?: ReactNode;
}) {
  const { t } = useTranslation('pnr');
  const destination = group.itinerary?.finalDestination ?? group.connection?.finalDestination ?? group.destination;
  const route = [group.origin, destination].filter(Boolean).join(' → ');

  // Alt+↑ / Alt+↓ walk the list from anywhere in the card.
  useEffect(() => {
    if (!onPrev && !onNext) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (!e.altKey) return;
      if (e.key === 'ArrowUp' && onPrev) {
        e.preventDefault();
        onPrev();
      } else if (e.key === 'ArrowDown' && onNext) {
        e.preventDefault();
        onNext();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onPrev, onNext]);

  const navButton = 'h-10 w-10 shrink-0 p-0';
  return (
    <header
      className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-2 border-b border-border py-3 pl-4 pr-16 sm:pl-6"
      data-testid="pnr-card-header"
    >
      <div className="flex min-w-0 flex-col">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="flex items-center gap-2">
            <span className="font-mono text-[28px] font-extrabold leading-none tracking-tight" data-testid="pnr-card-locator">
              {group.pnrLabel}
            </span>
            <PnrSourceMark source={group.pnrSource} className="shrink-0" testId="pnr-hero-source" />
          </span>
          <span className="text-[15px] text-muted-foreground" data-testid="pnr-card-summary">
            {t('card.header.summary', { count: group.paxCount, route })}
          </span>
        </div>
        {position ? (
          <span className="text-[13px] text-muted-foreground" data-testid="pnr-card-position">
            {position.listLabel
              ? t('card.header.positionInList', { index: position.index, total: position.total, list: position.listLabel })
              : t('card.header.position', { index: position.index, total: position.total })}
          </span>
        ) : null}
      </div>
      <div className="ml-auto flex items-center gap-1.5">
        {onPrev || onNext ? (
          <>
            <Button
              type="button"
              variant="outline"
              className={navButton}
              onClick={onPrev}
              disabled={!onPrev}
              aria-label={t('card.header.previous')}
              title={t('card.header.previousTitle')}
              data-testid="pnr-card-prev"
            >
              <ArrowUp className="h-4 w-4" aria-hidden />
            </Button>
            <Button
              type="button"
              variant="outline"
              className={navButton}
              onClick={onNext}
              disabled={!onNext}
              aria-label={t('card.header.next')}
              title={t('card.header.nextTitle')}
              data-testid="pnr-card-next"
            >
              <ArrowDown className="h-4 w-4" aria-hidden />
            </Button>
          </>
        ) : null}
        {moreActions}
      </div>
    </header>
  );
}
